import type {ComponentProps, PropsWithChildren} from 'react';
import {useRef, useCallback, useEffect, useState} from 'react';
import {useEventCallback} from 'usehooks-ts';
import classNames from 'classnames';

import Constants from 'utils/constants';

import {MenuOverlay} from './menu_overlay';

import './menu_wrapper.scss';

type Props = {
    id?: string;
    className?: string;
    open?: boolean;
    onToggle?: (open: boolean) => void;
    placement?: ComponentProps<typeof MenuOverlay>['placement'];
    offset?: ComponentProps<typeof MenuOverlay>['offset'];
    menuContainerClassName?: string;
};

const MenuWrapper = ({
    id,
    className,
    children,
    placement,
    offset,
    onToggle: onToggleMenu,
    open = false,
    menuContainerClassName,
}: PropsWithChildren<Props>) => {
    const [isOpen, setOpen] = useState(open);
    const trigger = useRef<HTMLDivElement>(null);
    const menuContainer = useRef<HTMLDivElement>(null);

    const toggleMenu = useEventCallback((value: boolean) => {
        setOpen((prevIsOpen) => {
            if (prevIsOpen !== value) {
                onToggleMenu?.(value);
            }

            return value;
        });
    });

    const handleTriggerPress = useCallback((event: MouseEvent | KeyboardEvent) => {
        event.preventDefault();
        event.stopPropagation();
        toggleMenu(!isOpen);
    }, [isOpen, toggleMenu]);

    const handleOnClose = useCallback(() => toggleMenu(false), [toggleMenu]);

    if (!Array.isArray(children) || children.length !== 2) {
        throw new Error('MenuWrapper needs exactly 2 children');
    }

    useEffect(() => {
        const abortController = new AbortController();
        if (trigger.current) {
            trigger.current.addEventListener('click', handleTriggerPress, {
                signal: abortController.signal,
                capture: true,
            });
            document.addEventListener('contextmenu', () => {
                toggleMenu(false);
            }, {
                signal: abortController.signal,
                capture: true,
            });
            trigger.current.addEventListener('keydown', (event) => {
                if (!trigger.current?.contains(document.activeElement)) {
                    return;
                }

                if (Constants.KeyCodes.ENTER[0] === event.key || Constants.KeyCodes.SPACE[0] === event.key) {
                    handleTriggerPress(event);
                }
            }, {signal: abortController.signal});
        }
        return () => {
            abortController.abort();
        };
    }, [handleTriggerPress, toggleMenu]);

    return (
        <div
            id={id}
            className={classNames('MenuWrapper', className, {'MenuWrapper--open': isOpen})}
            data-menu-open={isOpen}
            data-qa='post_dotmenu'
            data-testid='post_dotmenu'
        >
            <div ref={trigger}>{children ? Object.values(children)[0] : null}</div>
            {isOpen && (
                <MenuOverlay
                    isOpen={isOpen}
                    triggerRef={trigger}
                    placement={placement}
                    offset={offset}
                    onClose={handleOnClose}
                >
                    <div
                        ref={menuContainer}
                        className={classNames(menuContainerClassName, 'MenuWrapper-container')}
                    >{children ? Object.values(children)[1] : null}</div>
                </MenuOverlay>
            )}
        </div>
    );
};

export default MenuWrapper;
